import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Swal from "sweetalert2";
import {
  Stack,
  Button,
  Container,
  Grid,
  Input,
  InputAdornment,
  Accordion,
  AccordionSummary,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { debounce } from "../../../utils/debounce";
import config from "../../../config";
import i18n from "../../../i18n";
import { convertMessageCodeToMessage } from "../../../utils/messageCodeToMessage";
import Iconify from "../../../components/iconify/Iconify";
import {
  deleteContactRole,
  getContactRolesList,
} from "../../../services/contactRoles.service";
import ContactRolesTable from "src/SharedComponent/ContactRolesTable";

const TABLE_HEAD = [
  {
    id: "name",
    label: i18n.t("type"),
    alignRight: false,
    sortable: true,
  },
  { id: "" },
];

class ContactRoles extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      contactRoles: [],
      contactRoleDetails: {},
      page: 1,
      search: "",
      limit: 100,
      skip: 0,
      total_contactRoles: 0,
      tableLoader: false,
      pageLoader: false,
      orderBy: { sort_key: "name", sort_order: true },
      deleteContactRolesPopup: false,
    };
    this.useStyles = {
      sticky: {
        position: "sticky",
        left: "0",
        zIndex: "5",
      },
    };
  }

  async componentDidMount() {
    await this.getContactRolesData();
  }

  async setStateAsync(state) {
    return new Promise((resolve) => {
      this.setState(state, resolve);
    });
  }

  async getContactRolesData() {
    this.setState({
      tableLoader: true,
    });

    let query = {
      limit: this.state.limit,
      skip: this.state.skip,
      sort_key: this.state?.orderBy?.sort_key,
      sort_order: this.state?.orderBy?.sort_order ? "ASC" : "DESC",
    };

    if (this.state.search) {
      query.search = this.state.search;
    }

    let contactRoles = await getContactRolesList(query);
    if (contactRoles.error) {
      Swal.fire({
        icon: "error",
        title: i18n.t("error"),
        text: contactRoles?.error?.error_code
          ? i18n.t(convertMessageCodeToMessage(contactRoles?.error_code))
          : contactRoles?.error,
        confirmButtonText: i18n.t("ok"),
        confirmButtonColor: config.primaryColor,
      });
      this.setState({
        tableLoader: false,
      });
      return;
    }

    this.setState({
      tableLoader: false,
      contactRoles: contactRoles.person_roles,
      total_contactRoles:contactRoles?.count
    });
  }

  async handleDeleteContactRole(trainingCertificateId) {
    this.setState({
      pageLoader: true,
    });
    let response = await deleteContactRole(trainingCertificateId);

    if (response && response.error) {
      Swal.fire({
        icon: "error",
        title: i18n.t("error"),
        text: response?.error?.error_code
          ? i18n.t(convertMessageCodeToMessage(response?.error_code))
          : response?.error,
      });
      return;
    }

    if (response) {
      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "success",
        title: i18n.t("success"),
        text: response?.message_code
          ? i18n.t(convertMessageCodeToMessage(response?.message_code))
          : response?.message,
        showConfirmButton: false,
        timer: config.toastDelay,
        timerProgressBar: true,
      });
      await this.getContactRolesData();
      this.setState({
        pageLoader: false,
      });
    }
  }

  handleOpenMenu = (event) => {
    this.setState({ open: event.currentTarget });
  };

  handleCloseMenu = () => {
    this.setState({ open: null });
  };

  handleEdit = () => {
    this.setState({ open: null });
    this.props.navigate(
      "/settings/contact_roles/" + this.state?.contactRoleDetails?._id
    );
    this.handleCloseMenu();
  };
  

  getContactRolesDataOnchange = debounce(() => {
    this.getContactRolesData("filters");
  }, config.searchDebounceDelay);

  getContactRolesDataOnchangeWithoutDebounce = () => {
    this.getContactRolesData("filters");
  };

  handleSortAction = async (key) => {
    await this.setState({
      orderBy: {
        sort_key: key,
        sort_order: !this.state.orderBy?.sort_order,
      },
    });
    this.getContactRolesData();
  };

  handleActionMenu = (data, rowData) => {
    this.setState({
      contactRoleDetails: rowData,
    });
    this.handleOpenMenu(data);
  };

  handleDeleteTypeOfCertificate = async () => {
    this.handleCloseMenu();
    await this.setState({
      deleteContactRolesPopup: true,
    });
  };


  handlePagination=async(page)=>{
    await this.setState({
      skip: (page - 1) * this.state.limit,
      page
    });
    this.getContactRolesData();
  }

  handleLimitChange=async(limit)=>{
    await this.setState({
      limit,
      skip:0
    });
    this.getContactRolesData();
  }



  render() {
    return (
      <Container maxWidth="xxl" disableGutters>
        <Helmet>
          <title>
            {" "}
            {i18n.t("contactRoleDetails")} | {config.APPLICATION_NAME}{" "}
          </title>
        </Helmet>
        <Grid fullWidth>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <div style={{ width: "100%", marginRight: "10px" }}>
              <Accordion
                TransitionProps={{ unmountOnExit: true }}
                expanded={this.state.expanded}
                sx={{
                  background: "white",
                }}
              >
                <AccordionSummary
                  style={{ background: "white" }}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  sx={{ margin: 0 }}
                >
                  <Input
                    autoFocus
                    fullWidth
                    disableUnderline
                    placeholder="Search…"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Iconify
                          icon="eva:search-fill"
                          sx={{ color: "text.disabled", width: 20, height: 20 }}
                        />
                      </InputAdornment>
                    }
                    onChange={(e) => {
                      this.setState({
                        search: e?.target?.value,
                      });
                      this.getContactRolesDataOnchange();
                    }}
                    sx={{ mr: 1, fontWeight: "fontWeightBold" }}
                  />
                </AccordionSummary>
              </Accordion>
            </div>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              className="BtnWidth"
              onClick={() => {
                this.props.navigate("/settings/contact_roles/add");
              }}
            >
              {i18n.t("contact_role")}
            </Button>
          </Stack>
        </Grid>

        <ContactRolesTable
          data={this.state}
          handleEdit={this.handleEdit}
          handleSortAction={this.handleSortAction}
          handleOpenMenu={this.handleOpenMenu}
          handleCloseMenu={this.handleCloseMenu}
          handleActionMenu={this.handleActionMenu}
          handleDeleteTypeOfCertificate={this.handleDeleteTypeOfCertificate}
          handlePagination={this.handlePagination}
          handleLimitChange={this.handleLimitChange}
          tableHeaders={TABLE_HEAD}
        />

        <Dialog
          open={this.state?.deleteContactRolesPopup}
          onClose={() => {
            this.setState({
              deleteContactRolesPopup: false,
            });
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{i18n.t("delete")}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {i18n.t("are_you_sure_of_deleting_this_action_is_irreversible")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                this.setState({
                  deleteContactRolesPopup: false,
                });
              }}
              autoFocus
            >
              {i18n.t("cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                this.setState({
                  deleteContactRolesPopup: false,
                });
                this.handleDeleteContactRole(this.state.contactRoleDetails._id);
              }}
            >
              {i18n.t("proceed")}
            </Button>
          </DialogActions>
        </Dialog>

        <Backdrop
          sx={{
            color: "#e5e5e5",
            zIndex: (theme) => theme.zIndex.drawer + 1000,
          }}
          open={this.state?.pageLoader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    );
  }
}

// Wrap and export
export default function (props) {
  const navigate = useNavigate();
  const params = useParams();

  return <ContactRoles {...props} navigate={navigate} params={params} />;
}
